import React, { useState, useRef } from 'react';
import Papa from 'papaparse';
import { useJobContext } from '../context/JobContext';
import { useNavigate } from 'react-router-dom';
import { Input, Label } from '../Shared';

const CSVRemapper = ({goToNextTab}) => {
  const [files, setFiles] = useState([]);
  const [csvData, setCsvData] = useState({});
  const [headers, setHeaders] = useState({});
  const [remappedHeaders, setRemappedHeaders] = useState({});
  const [originalCSVs, setOriginalCSVs] = useState({});
  const [remappedCSVs, setRemappedCSVs] = useState({});
  const [count, setCount] = useState(0);
  const [usedHeaders, setUsedHeaders] = useState({});
  const [recordIndices, setRecordIndices] = useState({});
  const nav = useNavigate();
  const { finalCSVs, setFinalCSVs, sendum, formData, setFormData } = useJobContext();

  const [headerOptions, setHeaderOptions] = useState([
    'Select Header',
    'prefix',
    'firstName',
    'lastName',
    'middleName',
    'company',
    'suffix',
    'address1',
    'address2',
    'address3',
    'city',
    'state',
    'zip',
    'walkSequence',
    'carrierRoute',
    'custom1',
    'custom2',
    'custom3',
    'custom4',
    'custom5',
    'custom6',
    'custom7',
    'Custom8',
    'Custom9',
    'Custom10',
    'Custom11',
    'Custom12',
    'Custom13',
    'Custom14',
    'Custom15',
    'Custom16',
    'Custom17',
    'Custom18',
    'Custom19',
    'Custom20',
  ]);
  const [newHeaderInput, setNewHeaderInput] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // Target headers for final CSV (exclude 'Select Header')
  const targetHeaders = headerOptions.filter((header) => header !== 'Select Header');

  // Header mapping for auto-mapping (case-insensitive)
  const headerMapping = {
    prefix: ['prefix', 'pre', 'title'],
    firstName: ['first name', 'firstname', 'fname', 'given name'],
    lastName: ['last name', 'lastname', 'lname', 'surname', 'last_name'],
    middleName: ['middle name', 'middlename', 'mname'],
    suffix: ['suffix', 'sfx'],
    address1: ['address', 'address1', 'address 1', 'street', 'street1'],
    address2: ['address2', 'address 2', 'street2', 'apt', 'apartment'],
    address3: ['address3', 'address 3'],
    city: ['city', 'town'],
    state: ['state', 'st', 'province'],
    zip: ['zip', 'postal code', 'zipcode'],
    walkSequence: ['walk sequence', 'walksequence'],
    carrierRoute: ['carrier route', 'carrierroute'],
    custom1: ['custom1', 'cust1'],
    custom2: ['custom2', 'cust2'],
    custom3: ['custom3', 'cust3'],
    custom4: ['custom4', 'cust4'],
    custom5: ['custom5', 'cust5'],
    custom6: ['custom6', 'cust6'],
    custom7: ['custom7', 'cust7'],
    Custom8: ['custom8', 'cust8'],
    Custom9: ['custom9', 'cust9'],
    Custom10: ['custom10', 'cust10'],
    Custom11: ['custom11', 'cust11'],
    Custom12: ['custom12', 'cust12'],
    Custom13: ['custom13', 'cust13'],
    Custom14: ['custom14', 'cust14'],
    Custom15: ['custom15', 'cust15'],
    Custom16: ['custom16', 'cust16'],
    Custom17: ['custom17', 'cust17'],
    Custom18: ['custom18', 'cust18'],
    Custom19: ['custom19', 'cust19'],
    Custom20: ['custom20', 'cust20'],
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setError('');
    const droppedFiles = Array.from(e.dataTransfer.files).filter((file) => file.type === 'text/csv');
    if (droppedFiles.length === 0) {
      setError('Please upload valid CSV files.');
      return;
    }
    processFiles(droppedFiles);
  };

  const handleFileSelect = (e) => {
    setError('');
    const selectedFiles = Array.from(e.target.files).filter((file) => file.type === 'text/csv');
    if (selectedFiles.length === 0) {
      setError('Please upload valid CSV files.');
      return;
    }
    // Reset file input to allow re-uploading the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    // Clear previous state for files with the same name
    setFiles((prev) => prev.filter((f) => !selectedFiles.some((newFile) => newFile.name === f.name)));
    setHeaders((prev) => {
      const updated = { ...prev };
      selectedFiles.forEach((file) => delete updated[file.name]);
      return updated;
    });
    setCsvData((prev) => {
      const updated = { ...prev };
      selectedFiles.forEach((file) => delete updated[file.name]);
      return updated;
    });
    setRemappedHeaders((prev) => {
      const updated = { ...prev };
      selectedFiles.forEach((file) => delete updated[file.name]);
      return updated;
    });
    setOriginalCSVs((prev) => {
      const updated = { ...prev };
      selectedFiles.forEach((file) => delete updated[file.name]);
      return updated;
    });
    setRemappedCSVs((prev) => {
      const updated = { ...prev };
      selectedFiles.forEach((file) => delete updated[file.name]);
      return updated;
    });
    setFinalCSVs((prev) => {
      const updated = { ...prev };
      selectedFiles.forEach((file) => delete updated[file.name]);
      return updated;
    });
    setUsedHeaders((prev) => {
      const updated = { ...prev };
      selectedFiles.forEach((file) => delete updated[file.name]);
      return updated;
    });
    setRecordIndices((prev) => {
      const updated = { ...prev };
      selectedFiles.forEach((file) => delete updated[file.name]);
      return updated;
    });
    processFiles(selectedFiles);
  };

  const processFiles = (newFiles) => {
    newFiles.forEach((file) => {
      Papa.parse(file, {
        complete: (result) => {
          if (result.errors.length > 0) {
            setError(`Error parsing ${file.name}.`);
            return;
          }

          const data = result.data;

          if (data.length > 0) {
            setCount(data.length);
            const fileHeaders = data[0];
            setFiles((prev) => [...prev, file]);
            setHeaders((prev) => ({ ...prev, [file.name]: fileHeaders }));
            setCsvData((prev) => ({ ...prev, [file.name]: data }));
            setRecordIndices((prev) => ({ ...prev, [file.name]: 1 }));
            // Save original CSV
            const originalCsv = Papa.unparse(data);
            setOriginalCSVs((prev) => ({
              ...prev,
              [file.name]: new Blob([originalCsv], { type: 'text/csv;charset=utf-8;' }),
            }));

            // Auto-map headers based on headerMapping
            const initialRemappedHeaders = {};
            const initialUsedHeaders = [];
            fileHeaders.forEach((header) => {
              let mappedHeader = 'Select Header';
              // Check each target header's variations
              for (const targetHeader of Object.keys(headerMapping)) {
                if (
                  headerMapping[targetHeader].some(
                    (variation) => variation.toLowerCase() === header.toLowerCase()
                  ) &&
                  !initialUsedHeaders.includes(targetHeader) // Ensure not already used
                ) {
                  mappedHeader = targetHeader;
                  initialUsedHeaders.push(targetHeader);
                  break;
                }
              }
              initialRemappedHeaders[header] = mappedHeader;
            });

            setRemappedHeaders((prev) => ({
              ...prev,
              [file.name]: initialRemappedHeaders,
            }));
            setUsedHeaders((prev) => ({
              ...prev,
              [file.name]: initialUsedHeaders,
            }));

            // Initialize remapped CSV with auto-mapped headers
            updateRemappedCSV(file.name, data, fileHeaders);
          } else {
            setError(`CSV file ${file.name} is empty.`);
          }
        },
        header: false,
        skipEmptyLines: true,
      });
    });
    // Ensure state consistency after processing files
    setFiles((prevFiles) => {
      const fileNames = prevFiles.map((f) => f.name);
      setHeaders((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          if (!fileNames.includes(key)) delete updated[key];
        });
        return updated;
      });
      setCsvData((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          if (!fileNames.includes(key)) delete updated[key];
        });
        return updated;
      });
      setRemappedHeaders((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          if (!fileNames.includes(key)) delete updated[key];
        });
        return updated;
      });
      setOriginalCSVs((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          if (!fileNames.includes(key)) delete updated[key];
        });
        return updated;
      });
      setRemappedCSVs((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          if (!fileNames.includes(key)) delete updated[key];
        });
        return updated;
      });
      setFinalCSVs((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          if (!fileNames.includes(key)) delete updated[key];
        });
        return updated;
      });
      setUsedHeaders((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          if (!fileNames.includes(key)) delete updated[key];
        });
        return updated;
      });
      setRecordIndices((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          if (!fileNames.includes(key)) delete updated[key];
        });
        return updated;
      });
      return prevFiles;
    });
  };

  const updateRemappedCSV = (fileName, data, fileHeaders) => {
    if (!data || !fileHeaders || !remappedHeaders[fileName]) return;
    const newData = data.map((row, index) => {
      if (index === 0) {
        return fileHeaders.map((header) => {
          const mappedHeader = remappedHeaders[fileName]?.[header] || header;
          return mappedHeader === 'Select Header' ? header : mappedHeader;
        });
      }
      return row;
    });
    const csv = Papa.unparse(newData);
    setRemappedCSVs((prev) => ({
      ...prev,
      [fileName]: new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
    }));
  };

  const handleHeaderChange = (fileName, originalHeader, newHeader) => {
    setRemappedHeaders((prev) => {
      if (!prev[fileName]) return prev;
      const currentMappedHeader = prev[fileName][originalHeader];
      let updatedHeader = newHeader;

      // If clicking the current mapped header (not 'Select Header'), reset to 'Select Header'
      if (newHeader === currentMappedHeader && newHeader !== 'Select Header') {
        updatedHeader = 'Select Header';
      }

      const updated = { ...prev };

      // If the newHeader is used elsewhere, reset that specific mapping to 'Select Header'
      if (updatedHeader !== 'Select Header' && usedHeaders[fileName]?.includes(newHeader)) {
        Object.keys(updated[fileName]).forEach((otherHeader) => {
          if (otherHeader !== originalHeader && updated[fileName][otherHeader] === newHeader) {
            updated[fileName][otherHeader] = 'Select Header';
          }
        });
      }

      updated[fileName] = {
        ...updated[fileName],
        [originalHeader]: updatedHeader,
      };

      // Update used headers (exclude 'Select Header')
      setUsedHeaders((prev) => {
        const currentHeaders = prev[fileName] || [];
        const previousHeader = currentMappedHeader !== 'Select Header' ? currentMappedHeader : null;
        let newUsedHeaders = currentHeaders.filter((h) => h !== previousHeader);
        // Remove newHeader from usedHeaders if it was used elsewhere
        newUsedHeaders = newUsedHeaders.filter((h) => h !== newHeader);
        if (updatedHeader !== 'Select Header') {
          newUsedHeaders.push(updatedHeader);
        }
        return { ...prev, [fileName]: newUsedHeaders };
      });

      // Update remapped CSV
      updateRemappedCSV(fileName, csvData[fileName], headers[fileName]);
      return updated;
    });
  };

  const handleRemapToTarget = (fileName) => {
    const data = csvData[fileName];
    if (!data) return;

    const fileHeaders = headers[fileName];
    const headerMap = remappedHeaders[fileName];
    if (!fileHeaders || !headerMap) return;

    // Create a mapping from target headers to original headers
    const targetToOriginal = {};
    Object.keys(headerMap).forEach((original) => {
      const mapped = headerMap[original];
      if (targetHeaders.includes(mapped)) {
        targetToOriginal[mapped] = original;
      }
    });

    // Prepare new data with target headers
    const newData = [];
    // Add target headers as the first row
    newData.push(targetHeaders);

    // Map data rows (skip header row)
    for (let i = 1; i < data.length; i++) {
      const newRow = targetHeaders.map((targetHeader) => {
        const originalHeader = targetToOriginal[targetHeader];
        if (originalHeader) {
          const originalIndex = fileHeaders.indexOf(originalHeader);
          return data[i][originalIndex] || '';
        }
        return ''; // Empty for missing headers
      });
      newData.push(newRow);
    }

    // Generate and save final CSV
    const csv = Papa.unparse(newData);
    setFinalCSVs((prev) => ({
      ...prev,
      [fileName]: new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
    }));
  };

  const handleRemoveFile = (fileName) => {
    setFiles((prev) => prev.filter((file) => file.name !== fileName));
    setHeaders((prev) => {
      const { [fileName]: _, ...rest } = prev;
      return rest;
    });
    setCsvData((prev) => {
      const { [fileName]: _, ...rest } = prev;
      return rest;
    });
    setRemappedHeaders((prev) => {
      const { [fileName]: _, ...rest } = prev;
      return rest;
    });
    setOriginalCSVs((prev) => {
      const { [fileName]: _, ...rest } = prev;
      return rest;
    });
    setRemappedCSVs((prev) => {
      const { [fileName]: _, ...rest } = prev;
      return rest;
    });
    setFinalCSVs((prev) => {
      const { [fileName]: _, ...rest } = prev;
      return rest;
    });
    setUsedHeaders((prev) => {
      const { [fileName]: _, ...rest } = prev;
      return rest;
    });
    setRecordIndices((prev) => {
      const { [fileName]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleAddHeader = () => {
    if (newHeaderInput && !headerOptions.includes(newHeaderInput)) {
      setHeaderOptions((prev) => [...prev, newHeaderInput]);
      setNewHeaderInput('');
    }
  };

  const handleRemoveHeader = (header) => {
    setHeaderOptions((prev) => prev.filter((h) => h !== header));
    // Reset any mappings using the removed header
    setRemappedHeaders((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((fileName) => {
        Object.keys(updated[fileName]).forEach((originalHeader) => {
          if (updated[fileName][originalHeader] === header) {
            updated[fileName][originalHeader] = 'Select Header';
          }
        });
      });
      // Update used headers and remapped CSVs
      Object.keys(csvData).forEach((fileName) => {
        setUsedHeaders((prev) => {
          const newUsedHeaders = prev[fileName]?.filter((h) => h !== header) || [];
          return { ...prev, [fileName]: newUsedHeaders };
        });
        updateRemappedCSV(fileName, csvData[fileName], headers[fileName]);
      });
      return updated;
    });
  };

  const handleRecordNavigation = (fileName, direction) => {
    setRecordIndices((prev) => {
      const currentIndex = prev[fileName] || 1;
      const totalRecords = csvData[fileName]?.length || 1;
      let newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
      if (newIndex < 1) newIndex = 1; // Don't go before first record
      if (newIndex >= totalRecords) newIndex = totalRecords - 1; // Don't go past last record
      return { ...prev, [fileName]: newIndex };
    });
  };

  const downloadCSV = (blob, fileName) => {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
  };


  const savecsv = async () => {
    await sendum(count);
   goToNextTab()
  };

  return (
    <div className="min-h-screen bg-gray-100">
  
    <div className=" flex items-start justify-center p-3">
      <div className="bg-white p-4 rounded-lg shadow-lg w-full max-w-xl">
        <h1 className="text-xl font-bold mb-3 text-center">Upload list for presort</h1>
        <div
          className="border-2 border-dashed border-gray-300 p-4 rounded-lg text-center cursor-pointer hover:border-blue-500"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current.click()}
        >
          <p className="text-gray-500 text-xs">
            Drag and drop CSV files here or click to select
          </p>
          <Input
            type="file"
            accept=".csv"
            ref={fileInputRef}
            className="hidden"
            onChange={handleFileSelect}
            multiple
          />
        </div>
        {error && <p className="text-red-500 mt-1 text-xs">{error}</p>}
        {files.length > 0 && (
          <div className="mt-3">
            <h2 className="text-lg font-bold mb-1">Uploaded Files and Headers</h2>
            {files.map((file) => (
              // Defensive check to ensure file state exists
              headers[file.name] && csvData[file.name] && remappedHeaders[file.name] ? (
                <div key={file.name} className="mb-4">
                  <div className="flex justify-between items-center mb-1">
                    <h3 className="text-base font-semibold">{file.name}</h3>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleRemoveFile(file.name)}
                        className="px-3 py-0.5 text-red-500 hover:text-red-700 text-xs"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  <table className="w-full border-collapse text-xs">
                    <thead>
                      <tr className="bg-gray-200">
                        <th className="border p-0.5 w-1/3">Original Header</th>
                        <th className="border p-0.5 w-1/3">
                          <div className="flex justify-between items-center">
                            <span>Record {recordIndices[file.name] || 1}</span>
                            <div className="flex gap-0.5">
                              <button
                                onClick={() => handleRecordNavigation(file.name, 'prev')}
                                className="px-1.5 py-0.5 bg-gray-300 rounded hover:bg-gray-400 text-[10px]"
                                disabled={recordIndices[file.name] <= 1}
                              >
                                Prev
                              </button>
                              <button
                                onClick={() => handleRecordNavigation(file.name, 'next')}
                                className="px-1.5 py-0.5 bg-gray-300 rounded hover:bg-gray-400 text-[10px]"
                                disabled={
                                  recordIndices[file.name] >= (csvData[file.name]?.length - 1)
                                }
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        </th>
                        <th className="border p-0.5 w-1/3">Remapped Header</th>
                      </tr>
                    </thead>
                    <tbody>
                      {headers[file.name].map((header, index) => (
                        <tr key={header}>
                          <td className="border p-0.5 w-1/3">{header}</td>
                          <td className="border p-0.5 w-1/3">
                            {csvData[file.name]?.[recordIndices[file.name]]?.[index] || 'N/A'}
                          </td>
                          <td className="border p-0.5 w-1/3">
                            <select
                              value={remappedHeaders[file.name]?.[header] || 'Select Header'}
                              onChange={(e) => handleHeaderChange(file.name, header, e.target.value)}
                              className="w-full p-0.5 border rounded text-xs"
                            >
                              {headerOptions.map((option) => (
                                <option
                                  key={option}
                                  value={option}
                                  className={
                                    usedHeaders[file.name]?.includes(option) &&
                                    remappedHeaders[file.name]?.[header] !== option
                                      ? 'text-gray-500'
                                      : ''
                                  }
                                >
                                  {option}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-1 flex w-full">
                    <div className="flex-1">
                      <button
                      type='button'
                        onClick={() => handleRemapToTarget(file.name)}
                        className="px-3 py-0.5 bg-green-500 text-white rounded px-4 py-2 hover:bg-green-600 text-xs"
                      >
                        Remap to Target Headers
                      </button>
                    </div>
                    <div className="flex-1 text-right">
                      {finalCSVs[file.name] && (
                        <button
                          onClick={savecsv}
                          type="button"
                          className="bg-blue-500 text-white px-6 py-1 rounded"
                        >
                         {'Next ->'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div key={file.name} className="mb-4 text-red-500 text-xs">
                  Error: Data for {file.name} is not properly initialized. Please try re-uploading the file.
                </div>
              )
            ))}
          </div>
        )}
        <div className="mt-3 text-center">
          <button
            type="button"
            className="ml-2 bg-gray-500 text-white px-4 py-2 rounded-md"
           // onClick={() => nav('/dashboard')}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
         <div>
         <div className='py-6'> Options:</div>
          <div className='flex'>
     <div className="flex flex-1 items-center">
  <input
    type="checkbox"
    id="addon1"
    checked={formData?.addon1 === "Informed Delivery"}
    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
    onChange={(e) => setFormData({ ...formData, addon1: e.target.checked ? "Informed Delivery" : "" })}
  />
  <label htmlFor="addon1" className="ml-2 text-sm text-gray-700">
    Dedupe
  </label>
</div>
 <div className="flex flex-1 items-center">
  <input
    type="checkbox"
    id="addon1"
    checked={formData?.addon1 === "Informed Delivery"}
    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
    onChange={(e) => setFormData({ ...formData, addon1: e.target.checked ? "Informed Delivery" : "" })}
  />
  <label htmlFor="addon1" className="ml-2 text-sm text-gray-700">
    Remove Bad Address's
  </label>
</div>
 <div className="flex flex-1 items-center">
  <input
    type="checkbox"
    id="addon1"
    checked={formData?.addon1 === "Informed Delivery"}
    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
    onChange={(e) => setFormData({ ...formData, addon1: e.target.checked ? "Informed Delivery" : "" })}
  />
  <label htmlFor="addon1" className="ml-2 text-sm text-gray-700">
   Other
  </label>
</div>
 <div className="flex flex-1 items-center">
  <input
    type="checkbox"
    id="addon1"
    checked={formData?.addon1 === "Informed Delivery"}
    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
    onChange={(e) => setFormData({ ...formData, addon1: e.target.checked ? "Informed Delivery" : "" })}
  />
  <label htmlFor="addon1" className="ml-2 text-sm text-gray-700">
    Other
  </label>
</div>

          </div>
         </div>
    </div>
  );
};

export default CSVRemapper;